import {PlatformDataType} from 'src/interfaces/Platform.interface'

export type ConnectedStoreState = {
  mainStoreData: PlatformDataType | null
  accountingStoreData: PlatformDataType | null
  shippingStoreData: PlatformDataType | null
  erpStoreData: PlatformDataType | null
  omsStoreData: PlatformDataType | null
  ticketStoreData: PlatformDataType | null
}

export type SettingAvailability = {
  enabled: boolean
  disabledReason: string | null
}

const TOOLTIPS = {
  ORDER: 'This setting is only available when a source or OMS platform is connected.',
  ACCOUNTING_ORDER:
    'This setting is only available when an Accounting platform is connected.',
  ERP_ORDER: 'This setting is only available when an ERP platform is connected.',
  SHIPMENT:
    "This setting can be enabled when you are using Commercium's OMS or have connected an ERP, Shipping, or eCommerce platform.",
  BACK_SHIPMENT:
    "This setting can be enabled when you are using Commercium's OMS or have connected an ERP, Shipping, or eCommerce platform.",
  OMS_ORDER: 'This setting is only available when an OMS platform is connected.',
  TICKET: 'This setting is only available when a Helpdesk platform is connected.',
} as const

type RuleKey = keyof typeof TOOLTIPS

const hasPrimaryOrOmsSource = (stores: ConnectedStoreState): boolean =>
  Boolean(stores.mainStoreData || stores.omsStoreData)

const hasShipmentSource = (stores: ConnectedStoreState): boolean =>
  Boolean(
    stores.mainStoreData ||
      stores.erpStoreData ||
      stores.shippingStoreData ||
      stores.omsStoreData
  )

const RULE_CHECKS: Record<RuleKey, (stores: ConnectedStoreState) => boolean> = {
  ORDER: hasPrimaryOrOmsSource,
  ACCOUNTING_ORDER: (stores) => Boolean(stores.accountingStoreData),
  ERP_ORDER: (stores) => Boolean(stores.erpStoreData),
  SHIPMENT: hasShipmentSource,
  BACK_SHIPMENT: hasShipmentSource,
  OMS_ORDER: (stores) => Boolean(stores.omsStoreData || stores.mainStoreData?.isOmsStore),
  TICKET: (stores) => Boolean(stores.ticketStoreData),
}

const SETTING_TURN_OFF_MAP: Record<RuleKey, {containerKey: string; fieldName: string}> = {
  ORDER: {containerKey: 'order_setting', fieldName: 'isImportOrderToSource'},
  ACCOUNTING_ORDER: {
    containerKey: 'accounting_order_setting',
    fieldName: 'isImportOrderToSource',
  },
  ERP_ORDER: {containerKey: 'erp_order_setting', fieldName: 'isImportOrderToSource'},
  SHIPMENT: {containerKey: 'shipment_setting', fieldName: 'isImportShipmentFromMain'},
  BACK_SHIPMENT: {
    containerKey: 'back_shipment_setting',
    fieldName: 'isImportShipmentFromSecondaryPlatform',
  },
  OMS_ORDER: {containerKey: 'oms_order_setting', fieldName: 'isImportOrderToSource'},
  TICKET: {containerKey: 'ticket_setting', fieldName: 'isImportTicketsToSource'},
}

export const isChannelSettingRuleKey = (groupCode: string): groupCode is RuleKey =>
  groupCode in TOOLTIPS

export function getChannelSettingAvailability(
  groupCode: string,
  stores: ConnectedStoreState
): SettingAvailability {
  if (!isChannelSettingRuleKey(groupCode)) {
    return {enabled: true, disabledReason: null}
  }

  const isEnabled = RULE_CHECKS[groupCode](stores)
  return {
    enabled: isEnabled,
    disabledReason: isEnabled ? null : TOOLTIPS[groupCode],
  }
}

export function applyUnavailableChannelSettingTurnOff<T extends Record<string, any>>(
  values: T,
  stores: ConnectedStoreState
): T {
  const next: Record<string, any> = {...values}

  ;(Object.keys(SETTING_TURN_OFF_MAP) as RuleKey[]).forEach((groupCode) => {
    const availability = getChannelSettingAvailability(groupCode, stores)
    if (availability.enabled) return

    const {containerKey, fieldName} = SETTING_TURN_OFF_MAP[groupCode]
    if (!next[containerKey] || typeof next[containerKey] !== 'object') return

    next[containerKey] = {
      ...next[containerKey],
      [fieldName]: false,
    }
  })

  return next as T
}

export function buildConnectedStoreState({
  mainStoreData,
  accountingStoreData,
  shippingStoreData,
  erpStoreData,
  omsStoreData,
  ticketStoreData,
}: ConnectedStoreState): ConnectedStoreState {
  return {
    mainStoreData: mainStoreData ?? null,
    accountingStoreData: accountingStoreData ?? null,
    shippingStoreData: shippingStoreData ?? null,
    erpStoreData: erpStoreData ?? null,
    omsStoreData: omsStoreData ?? null,
    ticketStoreData: ticketStoreData ?? null,
  }
}
